import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => localStorage.getItem('token');

const referenceValuesApiClient = axios.create({
  baseURL: config.api.referenceValuesServiceUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

referenceValuesApiClient.interceptors.request.use(
  (cfg) => {
    const token = getToken();
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error),
);

applyAuthRetryInterceptor(referenceValuesApiClient);
applyTokenRefreshInterceptor(referenceValuesApiClient);

const BASE = '/reference-values';

/** The most values one review request may name (the service's limit). */
export const MAX_REVIEW_ITEMS = 200;

const referenceValuesService = {
  /** { current_year, catalog: { categories, values }, values: [row] } */
  list: async () => {
    const response = await referenceValuesApiClient.get(BASE);
    return response.data;
  },

  /** The latest refresh runs, newest first. */
  runs: async (limit = 5) => {
    const response = await referenceValuesApiClient.get(`${BASE}/runs`, { params: { limit } });
    return response.data.runs;
  },

  /**
   * Approve or reject proposals. items: [{ year, key, expected_value }] -- the
   * proposed value as shown, so a proposal replaced since is refused per item.
   * Returns per-item results [{ year, key, ok, error }].
   */
  review: async (items, action, note) => {
    const response = await referenceValuesApiClient.post(`${BASE}/review`, { items, action, note: note || null });
    return response.data.results;
  },

  /** Publish a value by hand: { value, source_ref, source_url, note, expected_updated_at }. */
  setValue: async (year, key, body) => {
    const response = await referenceValuesApiClient.put(
      `${BASE}/${encodeURIComponent(year)}/${encodeURIComponent(key)}`,
      body,
    );
    return response.data;
  },

  /** Start a refresh now; it runs in the background and shows up in runs(). */
  refresh: async () => {
    const response = await referenceValuesApiClient.post(`${BASE}/refresh`);
    return response.data;
  },
};

export default referenceValuesService;
